import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table: CodeCategories
 *
 * Displays a list of code categories within a DataTable.
 *
 * @param error
 * @param isLoading
 * @param requestArgs
 * @param requestFunction
 * @param requestKey
 * @param rows
 * @param setCurrentEntity
 * @param setIsFormModalOpen
 * @param totalPages
 * @param totalRecords
 * @returns {Element}
 * @constructor
 */
export function CodeCategories({
  error = null,
  isLoading = false,
  requestArgs = {},
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  setCurrentEntity = () => {},
  setIsFormModalOpen = () => {},
  totalPages = 0,
  totalRecords = 0,
}) {
  const { hasPermission } = useCurrentUser();

  const canEdit = hasPermission('Admin:CodeCategories', 'edit');

  /**
   * renderCellActions
   *
   * Provides the user with a menu of actionable items to perform on a specific action path within the list of action
   * paths.
   *
   * @param row
   * @returns {Element}
   */
  const renderCellActions = ({ row }) => {
    const menuItems = [
      {
        disabled: !canEdit,
        icon: <IconSymbol icon="edit" />,
        onClick: () => {
          setIsFormModalOpen(true);
          setCurrentEntity(row?.original);
        },
        title: 'Edit',
      },
    ];

    return (
      <div
        style={{
          marginLeft: '-10px',
          marginRight: '-10px',
        }}
      >
        <Layout.Group
          css={{
            height: '100%',
            marginLeft: '2px',
            marginRight: '2px',
          }}
        >
          <DropdownMenu
            before={<IconSymbol color="#6F6F6F" icon="more_vert" size={24} />}
            hideLabel
            menuItems={menuItems}
          />
        </Layout.Group>
      </div>
    );
  };

  /**
   * renderCellIsValid
   *
   * display the value of the isValid field as a string
   *
   * @param value
   * @returns {string}
   */
  const renderCellIsValid = ({ value }) => {
    return String(value).toUpperCase();
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc']).map((item) => {
      const column = item;

      if (column.accessor === 'actions') {
        column.Cell = renderCellActions;
      }

      if (column.accessor === 'isValid') {
        column.Cell = renderCellIsValid;
      }

      return column;
    });
  }, [canEdit]);

  return (
    <ErrorHandler location="src/entities/Code/ui/components/tables/CodeCategories/CodeCategories.jsx">
      <Styles>
        <TableComponent
          {...{
            columns,
            configuration,
            error,
            isLoading,
            noDataMessage: 'No code categories found.',
            requestArgs,
            requestFunction,
            requestKey,
            rows,
            totalPages,
            totalRecords,
          }}
        />
      </Styles>
    </ErrorHandler>
  );
}
